import Modal from '@common/Modal';
import { Button, Collapse, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import AuditTrailsTable from './AuditTrailsTable';
import { selectActivitySlice } from '@features/activity/redux/slice';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import { useState } from 'react';
import apiClient, { getLocalToken } from '@utils/apiClient';

export default function AuditTrails({
  isOpen,
  onClose,
  activityList,
  project,
  partner,
}) {
  const dispatch = useDispatch();
  const { auditTrailsLoading, auditTrailsData } =
    useSelector(selectActivitySlice);
  const [downloadingAuditTrail, setDownloadingAuditTrail] = useState(false);

  // transforming activity trail if activity is of partner
  const trails = auditTrailsData?.length
    ? auditTrailsData?.reduce((acc, item) => {
        if (!acc[item.activity_id]) {
          acc[item.activity_id] = [];
        }
        acc[item.activity_id].push(item);
        return acc;
      }, {})
    : {};

  //rendering audit trails for partner
  const renderedPartnerAuditTrails = Object.entries(trails)?.map(
    ([id, trail]) => {
      return (
        <Collapse
          key={id}
          className="!bg-[#F9FAFB] !font-segoe-ui !text-md"
          expandIconPosition="end"
          items={[
            {
              key: '1',
              label:
                activityList.find((act) => act.id === Number(id))
                  ?.activity_description || 'NA',
              children: (
                <AuditTrailsTable data={trail} loading={auditTrailsLoading} />
              ),
              classNames: {
                body: 'border border-gray-200 !p-0 hide-footer',
                header: '!p-3',
              },
            },
          ]}
          bordered={false}
          defaultActiveKey={['1']}
        />
      );
    },
  );

  const handleTrailDownload = async () => {
    setDownloadingAuditTrail(true);
    // Project Name_Partner Type_All Activity Audit Trial
    // Project Name_Partner Type_Activity Audit Trial
    try {
      const response = await fetch(
        `${apiClient.Urls.apiUrl}/activity/${isOpen?.isSingleActivity ? 'download-audit-trails-by-id/' : 'download-audit-trails-by-partner-id/'}${isOpen?.id}`,
        {
          headers: {
            Authorization: getLocalToken(),
          },
        },
      );
      if (!response.ok) {
        throw new Error('Failed to download activity trail...');
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;

      link.setAttribute(
        'download',
        isOpen?.isSingleActivity
          ? `${project?.project_name}_${partner?.partner_type}_Activity Audit Trial.xlsx`
          : `${project?.project_name}_${partner?.partner_type}_All Activity Audit Trial.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadingAuditTrail(false);
      message.error(error.message || 'Failed to download activity trail...');
    } finally {
      setDownloadingAuditTrail(false);
    }
  };

  return (
    <Modal
      footerComponent={' '}
      btnLoading={auditTrailsLoading}
      header={
        <div className="flex items-center justify-between p-0">
          <h3
            className={'text-lg font-semibold text-tertiary-text font-segoe-ui'}
          >
            Audit Trail
          </h3>
          <div className="flex items-center gap-4">
            <Button
              className="bg-transparent !text-xs !m-0 !px-2 !py-0"
              icon={<DownloadOutlined />}
              onClick={handleTrailDownload}
              variant="outlined"
              loading={downloadingAuditTrail}
            >
              Download
            </Button>
            <Button
              className="bg-transparent border-none !m-0 !p-0"
              icon={<CloseOutlined />}
              shape="circle"
              onClick={onClose}
            />
          </div>
        </div>
      }
      isOpen={Boolean(isOpen?.id)}
      onClose={onClose}
      spanWidth={'!w-[90vw] md:!w-[80vw] lg:!w-[75vw]'}
      centered
    >
      {isOpen?.isSingleActivity ? (
        <Collapse
          className="!bg-[#F9FAFB] !font-segoe-ui !text-md"
          expandIconPosition="end"
          items={[
            {
              key: '1',
              label:
                activityList.find((act) => act.id === isOpen?.id)
                  ?.activity_description || 'NA',
              children: (
                <AuditTrailsTable
                  data={auditTrailsData}
                  loading={auditTrailsLoading}
                />
              ),
              classNames: {
                body: 'border border-gray-200 !p-0 hide-footer',
                header: '!p-3',
              },
            },
          ]}
          bordered={false}
          defaultActiveKey={['1']}
        />
      ) : (
        <div className="flex flex-col gap-4">{renderedPartnerAuditTrails}</div>
      )}
    </Modal>
  );
}
